import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {getComments, getCommentSnippet} from '../util/thread-comment-accessors'
import styles from './CompactThread.module.css'

function CompactComment({
  comment,
  emphasized = false,
}: {
  comment: ReturnType<typeof getComments>[number]
  emphasized?: boolean
}) {
  const login = comment.author?.login ?? 'ghost'
  return (
    <div className={styles.commentRow}>
      <GitHubAvatar src={comment.author?.avatarUrl ?? ''} size={16} alt="" className={styles.avatar} />
      <span className={emphasized ? styles.authorEmphasized : styles.author}>{login}</span>
      <span className={styles.snippet}>{getCommentSnippet(comment)}</span>
    </div>
  )
}

/**
 * A condensed, always-visible preview of a comment thread shown below the
 * collapsed ForYouItem header. The file name and lines are surfaced in the
 * header; this preview shows the initial comment, a "N more replies" divider
 * for any middle comments, and the latest reply. Clicking the parent item
 * expands to the full thread interface (which shows the full file path).
 */
export function CompactThread({thread}: {thread: Thread}) {
  const comments = getComments(thread)
  const initial = comments[0]
  const latest = comments[comments.length - 1]
  if (!initial || !latest) return null

  const hasReply = comments.length > 1
  const hiddenReplyCount = Math.max(comments.length - 2, 0)

  return (
    <div className={styles.container}>
      <div className={styles.comments}>
        <CompactComment comment={initial} />
        {hiddenReplyCount > 0 && (
          <div className={styles.moreReplies}>
            <span>{`${hiddenReplyCount} more ${hiddenReplyCount === 1 ? 'reply' : 'replies'}`}</span>
          </div>
        )}
        {hasReply && <CompactComment comment={latest} emphasized />}
      </div>
    </div>
  )
}
